import { useEffect, useRef, useState } from 'react'
import style from './Chat.module.css'
import ChatInput from './ChatInput'
import ChatInterface from './ChatInterface'
import useDraggable from '../../../hooks/useDraggable'
import { useFilesContext } from '../../../contexts/FilesContext'
import DropZoneIcon from '../../../assets/svgs/DropIcon'
import { useHistoryContext } from '../../../contexts/HistoryContext'
import { logEvent } from '../../../utils/logEvent'
import Cookies from 'js-cookie'
import FetchData from '../../../utils/FetchData'

const token = Cookies.get('token')

const Chat = ({ styles }) => {
  // formData remains only for the main message input (no changes to existing behavior)
  const [formData, setFormData] = useState({ message: '', cardUrl: '' })
  const [feedFaq, setFeedFaq] = useState([])
  const [openUpload, setOpenUpload] = useState(false) // reused for opening the Tools dropdown
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Tools state
  const [activeTool, setActiveTool] = useState(null) // 'jobFitCheck' | null
  const [jobFitUrl, setJobFitUrl] = useState('')     // input when Job Fit Check is enabled and no feed items

  // Context
  const { addRequest, addResponse, history, updateLastResponse, persistLastResponse } =
    useHistoryContext()
  const { isDragging } = useDraggable()
  const { addFile, files, setFiles } = useFilesContext()

  // Ensure we only trigger the auto "Hi" once per mount when history is empty
  const introSeededRef = useRef(false)

  useEffect(() => {
    const fetchFeedFaq = async (categories) => {
      const data = await FetchData.feedFaq(categories)
      setFeedFaq(data)
    }

    const fileCategories = [...new Set(files.map((file) => file.category))]
    fetchFeedFaq(fileCategories)
  }, [files])

  // Derived booleans for tool UX
  const hasFiles = files.length > 0

  // Auto-disable Job Fit Check if ANY item is present


  // When a draggable card is dropped, update formData with its URL and enforce tool rules
  const handleDrop = (e) => {
    e.preventDefault()

    console.log('types:', [...e.dataTransfer.types]);
    const data = e.dataTransfer.getData('application/json')
    if (data) {
      const item = JSON.parse(data)

      // Intelligent Drop: If Job AND Tool is active, populate Job Fit Check
      if (activeTool === 'jobFitCheck' && (item.category === 'Jobs' || item.title?.toLowerCase().includes('job'))) {
        setJobFitUrl(item.url || '')
        return
      }

      // Default: Update hidden cardUrl and add file
      setFormData((prev) => ({ ...prev, cardUrl: item.url }))
      addFile(item)

      logEvent({
        action: `drag_${item.category}`,
        content: item.title,
      })
    }
  }

  const handleDragOver = (e) => {
    // If URL typed, visually indicate we won't accept drops
    if (jobFitUrl.trim().length > 0) {
      e.dataTransfer.dropEffect = 'none'
    }
    e.preventDefault()
  }

  // Sending message to backend with added fields jobfiturl & itemcategory
  // opts.silentUser = true => do NOT show the user's message (used for auto "Hi")
  async function handleResponses(message, opts = {}) {
    const { silentUser = false } = opts

    if (!message) {
      setErrorMessage('Oops! Looks like you forgot to type your message')
      return
    }

    setErrorMessage('')
    setLoading(true)
    if (!silentUser) {
      addRequest(message) // visible user message only when not silent
    }

    const items = files.map(({ id, url, category }) => ({ id, url, category }))

    // Parallel arrays, if you want them separately as well
    const filesUrls = items.map(it => it.url)
    const itemIds = items.map(it => it.id)
    const itemCategories = items.map(it => it.category)

    // Determine jobfiturl to send (only if Job Fit Check is enabled)
    const jobfiturl = activeTool === 'jobFitCheck' ? (jobFitUrl || '') : ''

    // Clear local files (keeps your existing behavior)
    setFiles([])

    // Reset Job Fit Tool if it was active
    if (activeTool === 'jobFitCheck') {
      setActiveTool(null)
      setJobFitUrl('')
    }

    try {
      const payload = { message, urls: filesUrls, jobfiturl, itemIds, itemCategories }

      // Pretty + raw logs (mask token in logs)
      console.log('[POST] /chat headers:', {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token ? token.slice(0, 6) + '…' : ''}`,
      });
      console.log('[POST] /chat payload (object):', payload);
      console.log('[POST] /chat payload (json):', JSON.stringify(payload, null, 2));

      const response = await fetch(process.env.REACT_APP_CHAT_API_URL || 'https://chimpknows.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok || !response.body) throw new Error('Stream failed')

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')

      let buffer = ''
      let fullText = ''

      setLoading(false)
      // Start a new assistant message to stream into
      addResponse('', undefined, { persist: false })

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const json = JSON.parse(line.slice(6).trim())
              if (json.content) {
                fullText += json.content
                updateLastResponse(fullText)
              }
            } catch (err) {
              // ignore malformed SSE chunks
            }
          }
        }
      }
      persistLastResponse()
    } catch (error) {
      console.error('Error streaming response:', error)
      setLoading(false)
      persistLastResponse()
    }
  }

  // Auto-start the conversation with "Hi" on a brand-new chat (once per mount),
  // without showing the user's "Hi" message in the UI.
  useEffect(() => {
    if (history.length === 0 && !introSeededRef.current) {
      introSeededRef.current = true
      handleResponses('Hi', { silentUser: true })
    }
    // We intentionally only depend on history length here to avoid re-firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.length])

  // Pill UI clear
  const clearJobFitTool = () => {
    setActiveTool(null)
    setJobFitUrl('')
    logEvent({ action: 'tool_disabled', content: 'Job Fit Check (pill x)' })
  }

  return (
    <div
      className={style.container}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      style={{ ...styles }}
    >
      {history.length > 0 ? (
        <ChatInterface
          smallInterface={true}
          history={history}
          loading={loading}
          handleResponses={handleResponses}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          {!isDragging ? (
            <div className={style.cardsContainer}>
              {feedFaq.map((faq, index) => (
                <div
                  className={style.card}
                  key={index}
                  onClick={() => handleResponses(faq.question)}
                >
                  <p className={style.cardTitle}>{faq.question}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className={style.dropZone}>
              <DropZoneIcon />
              <p className={style.dropZoneText}>
                Drag & drop items from dashboard to discuss with Chimp
              </p>
            </div>
          )}
        </div>
      )}

      {/* CHAT INPUT (Plus icon opens the Tools dropdown) */}
      <ChatInput
        setOpenUpload={setOpenUpload}
        openUpload={openUpload}
        handleResponses={handleResponses}
        setFormData={setFormData}
        formData={formData}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        // Tool props
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        jobToolDisabled={hasFiles}  // disable tool if ANY item attached
      />

      {/* JOB FIT CHECK AREA */}
      {activeTool === 'jobFitCheck' && (
        <div style={{
          marginTop: 12,
          marginBottom: 12,
          border: '1px dashed #61487c',
          backgroundColor: 'rgba(142, 70, 221, 0.05)',
          borderRadius: 12,
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          position: 'relative'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#432c5b' }}>Job Fit Check</span>
              {jobFitUrl.length > 0 && <span style={{ fontSize: 10, color: '#4caf50', fontWeight: 600 }}>READY</span>}
            </div>
            <button
              onClick={clearJobFitTool}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666', fontSize: 18, padding: 0, lineHeight: 0.5 }}
            >
              ×
            </button>
          </div>

          {/* Input / Drop Display */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 8,
              padding: '8px 12px'
            }}>
              {/* Icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#61487c" strokeWidth="2" style={{ marginRight: 8, minWidth: 16 }}>
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
              </svg>

              <input
                placeholder="Drag job here or paste URL"
                value={jobFitUrl}
                onChange={(e) => setJobFitUrl(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  fontSize: 13,
                  width: '100%',
                  color: '#333'
                }}
              />
            </div>
          </div>

          {/* Helper Text */}
          {jobFitUrl.length === 0 && (
            <p style={{ margin: 0, fontSize: 11, color: '#888', fontStyle: 'italic', paddingLeft: 4 }}>
              Tip: Drag a job card from your feed directly here!
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default Chat
